import { useState, useEffect } from 'react'
import { useAgentFiles } from '../../hooks/useAgentFiles'
import { useAgentFileNavigation } from '../../hooks/useAgentFileNavigation'
import { FileTree } from './FileTree'
// import { CodeMirrorEditor } from '../CodeMirrorEditor' // Deprecated
import { MonacoEditor } from '../MonacoEditor'
import { MarkdownPreview } from '../MarkdownPreview'
import { Breadcrumbs } from '../Breadcrumbs'
import { FileTabs, type FileTab } from '../FileTabs'
import { StatusBar } from '../StatusBar'
import { ContextMenu } from '../ContextMenu'
import { HeaderActions } from '../HeaderActions'
import { NewFileModal } from '../NewFileModal'
import { DeleteConfirmModal } from '../DeleteConfirmModal'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { isEssentialFile, isEssentialFolder } from '../../utils/essentialFiles'
import { AlertCircle, Loader2, Save, Eye, EyeOff, Trash2, MoreVertical } from 'lucide-react'

interface AgentFileEditorProps {
    agentId: string
    fileManager?: ReturnType<typeof useAgentFiles>
    hideSidebar?: boolean
    className?: string
}

export function AgentFileEditor({ agentId, fileManager: externalFileManager, hideSidebar = false, className = '' }: AgentFileEditorProps) {
    const internalFileManager = useAgentFiles(agentId)
    const fileManager = externalFileManager || internalFileManager

    const [showPreview, setShowPreview] = useState(false)
    const [openTabs, setOpenTabs] = useState<FileTab[]>([])
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
    const [showSuccessMessage, setShowSuccessMessage] = useState(false)
    const [showNewFileModal, setShowNewFileModal] = useState(false)
    const [showNewFolderModal, setShowNewFolderModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [fileToDelete, setFileToDelete] = useState<string | null>(null)

    // URL-based file navigation - ONLY if not using external file manager (to avoid conflicts)
    const { currentFileFromUrl, navigateToFile } = useAgentFileNavigation(agentId, 'configuracao')

    const {
        files = [],
        currentFile = null,
        content = '',
        isLoading = false,
        isSaving = false,
        isDirty = false,
        error = null,
        openFile = () => { },
        saveFile = () => { },
        createFile = () => { },
        deleteFile = () => { },
        updateContent = () => { }
    } = fileManager || {}

    // Sync URL with file selection on mount and URL change - ONLY if internal manager
    useEffect(() => {
        if (!externalFileManager && currentFileFromUrl && currentFileFromUrl !== currentFile) {
            openFile(currentFileFromUrl)
        }
    }, [currentFileFromUrl, externalFileManager, currentFile, openFile])

    // Add new tab when file is opened
    useEffect(() => {
        if (currentFile && !openTabs.find(tab => tab.path === currentFile)) {
            const fileName = currentFile.split('/').pop() || currentFile
            // eslint-disable-next-line
            setOpenTabs(prev => [...prev, {
                path: currentFile,
                name: fileName,
                isDirty: false,
                isActive: true
            }])
        }
    }, [currentFile, openTabs])

    // Update active tab and dirty status
    useEffect(() => {
        // eslint-disable-next-line
        setOpenTabs(prev => prev.map(tab => ({
            ...tab,
            isActive: tab.path === currentFile,
            isDirty: tab.path === currentFile ? isDirty : tab.isDirty
        })))
    }, [currentFile, isDirty])

    const handleTabClick = (path: string) => {
        openFile(path)
    }

    const handleTabClose = (path: string) => {
        setOpenTabs(prev => prev.filter(tab => tab.path !== path))
        if (path === currentFile && openTabs.length > 1) {
            const remainingTabs = openTabs.filter(tab => tab.path !== path)
            if (remainingTabs.length > 0) {
                openFile(remainingTabs[0].path)
            }
        }
    }

    const handleBreadcrumbNavigate = (path: string) => {
        // Navigate to folder - could expand file tree in future
        console.log('Navigate to:', path)
    }

    const handleFileSelectFromFolder = (filePath: string) => {
        // Only update URL. The useEffect below will handle opening the file
        // when the URL changes (if operating in internal mode).
        // If we are in external mode, we MUST call openFile.
        if (externalFileManager) {
            openFile(filePath)
        } else {
            navigateToFile(filePath)
        }
    }

    // Keyboard shortcuts
    useKeyboardShortcuts({
        shortcuts: [
            {
                key: 'Tab',
                ctrl: true,
                action: () => {
                    // Next tab
                    const currentIndex = openTabs.findIndex(tab => tab.isActive)
                    if (currentIndex < openTabs.length - 1) {
                        openFile(openTabs[currentIndex + 1].path)
                    } else if (openTabs.length > 0) {
                        openFile(openTabs[0].path)
                    }
                },
                description: 'Next tab'
            },
            {
                key: 'Tab',
                ctrl: true,
                shift: true,
                action: () => {
                    // Previous tab
                    const currentIndex = openTabs.findIndex(tab => tab.isActive)
                    if (currentIndex > 0) {
                        openFile(openTabs[currentIndex - 1].path)
                    } else if (openTabs.length > 0) {
                        openFile(openTabs[openTabs.length - 1].path)
                    }
                },
                description: 'Previous tab'
            },
            {
                key: 'w',
                ctrl: true,
                action: () => {
                    // Close current tab
                    if (currentFile) {
                        handleTabClose(currentFile)
                    }
                },
                description: 'Close tab'
            },
            {
                key: 'e',
                ctrl: true,
                shift: true,
                action: () => {
                    // Toggle preview
                    setShowPreview(prev => !prev)
                },
                description: 'Toggle preview'
            }
        ]
    })

    // Atalho Ctrl+S para salvar
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault()
                if (currentFile && isDirty) {
                    saveFile()
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [currentFile, isDirty, saveFile])

    if (!fileManager) {
        return null
    }

    const handleDelete = () => {
        if (!currentFile) return

        // Check if file is essential and cannot be deleted
        if (isEssentialFile(currentFile)) {
            alert('Este arquivo faz parte da estrutura essencial do agente e não pode ser deletado. Você pode editá-lo, mas não removê-lo.')
            return
        }

        // Open secure delete modal
        setFileToDelete(currentFile)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!fileToDelete) return

        // Double-check if file is essential (safety check)
        if (isEssentialFile(fileToDelete)) {
            alert('Este arquivo faz parte da estrutura essencial do agente e não pode ser deletado.')
            setFileToDelete(null)
            setShowDeleteModal(false)
            return
        }

        try {
            await deleteFile(fileToDelete)
            setFileToDelete(null)
        } catch (err) {
            console.error('Error deleting file:', err)
            alert('Erro ao deletar arquivo. Verifique o console.')
        }
    }

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault()
        setContextMenu({ x: e.clientX, y: e.clientY })
    }

    const handleCopySuccess = () => {
        setShowSuccessMessage(true)
        setTimeout(() => setShowSuccessMessage(false), 2000)
    }

    const handleDownloadSuccess = () => {
        setShowSuccessMessage(true)
        setTimeout(() => setShowSuccessMessage(false), 2000)
    }

    return (
        <div className={`flex flex-col h-full bg-white dark:bg-black ${className}`}>
            {/* Breadcrumbs */}
            {currentFile && (
                <Breadcrumbs
                    path={currentFile}
                    onNavigate={handleBreadcrumbNavigate}
                />
            )}

            {/* File Tabs */}
            <FileTabs
                tabs={openTabs}
                onTabClick={handleTabClick}
                onTabClose={handleTabClose}
            />

            {/* Header com ações */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-black">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {currentFile || 'Nenhum arquivo selecionado'}
                    </span>
                    {isDirty && (
                        <span className="text-xs text-orange-500 dark:text-orange-400 flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                            Não salvo
                        </span>
                    )}
                </div>

                {/* Ações do arquivo */}
                {currentFile && (
                    <div className="flex items-center gap-2" onContextMenu={handleContextMenu}>
                        <HeaderActions
                            content={content}
                            fileName={currentFile.split('/').pop() || 'file.md'}
                            onCopy={handleCopySuccess}
                            onDownload={handleDownloadSuccess}
                        />
                        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
                        <button
                            onClick={() => setShowPreview(!showPreview)}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                            title={showPreview ? 'Ocultar Preview (Ctrl+Shift+E)' : 'Mostrar Preview (Ctrl+Shift+E)'}
                        >
                            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                            onClick={() => saveFile()}
                            disabled={!isDirty || isSaving}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed rounded-md transition-colors"
                            title="Salvar (Ctrl+S)"
                        >
                            <Save className="w-4 h-4" />
                            {isSaving ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button
                            onClick={handleDelete}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                            title="Deletar arquivo"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* Error message */}
            {error && (
                <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-2 text-sm text-red-800 dark:text-red-200">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden relative">
                {/* File tree sidebar - always visible if not hidden by prop */}
                {!hideSidebar && (
                    <div
                        className="
                        border-r border-gray-200 dark:border-gray-800 
                        bg-white dark:bg-black
                        w-64 min-w-[16rem]
                        overflow-hidden
                    "
                    >
                        <FileTree
                            files={files}
                            currentFile={currentFile}
                            onFileSelect={handleFileSelectFromFolder}
                            onNewFile={() => setShowNewFileModal(true)}
                            onNewFolder={() => setShowNewFolderModal(true)}
                            onDeleteFolder={(folderPath) => {
                                // Check if folder is essential and cannot be deleted
                                if (isEssentialFolder(folderPath)) {
                                    alert('Esta pasta faz parte da estrutura essencial do agente e não pode ser deletada.')
                                    return
                                }
                                setFileToDelete(folderPath)
                                setShowDeleteModal(true)
                            }}
                        />
                    </div>
                )}

                {/* Editor and preview */}
                {currentFile ? (
                    <div className="flex-1 flex overflow-hidden relative">
                        {/* Editor */}
                        <div className={`${showPreview ? 'w-1/2' : 'w-full'} h-full`}>
                            {isLoading ? (
                                <div className="flex items-center justify-center h-full">
                                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                                </div>
                            ) : (
                                <MonacoEditor
                                    key={currentFile}
                                    value={content}
                                    onChange={updateContent}
                                    language="markdown"
                                />
                            )}
                        </div>

                        {/* Preview */}
                        {showPreview && (
                            <div className="w-1/2 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
                                <MarkdownPreview
                                    content={content}
                                />
                            </div>
                        )}

                        {/* Floating action button */}
                        <div className="absolute bottom-4 right-4 flex items-center gap-2">
                            {isSaving && (
                                <div className="px-3 py-2 bg-gray-800 dark:bg-gray-700 text-white text-xs rounded-md shadow-lg flex items-center gap-2">
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Salvando...
                                </div>
                            )}
                            <button
                                className="p-2 bg-gray-800 dark:bg-gray-700 text-white hover:bg-gray-700 dark:hover:bg-gray-600 rounded-md shadow-lg transition-colors"
                                title="Mais opções"
                            >
                                <MoreVertical className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                        <p>Selecione um arquivo ou pasta para visualizar</p>
                    </div>
                )}
            </div>

            {/* Status Bar */}
            {currentFile && (
                <StatusBar
                    fileName={currentFile.split('/').pop()}
                    fileSize={content.length}
                    totalLines={content.split('\n').length}
                    language="Markdown"
                    encoding="UTF-8"
                    lineEnding="LF"
                />
            )}

            {/* Context Menu */}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onClose={() => setContextMenu(null)}
                    onCopy={handleCopySuccess}
                    onDownload={handleDownloadSuccess}
                    onDelete={handleDelete}
                />
            )}

            {/* Success Message */}
            {showSuccessMessage && (
                <div className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-md shadow-lg z-50 animate-fade-in">
                    ✓ Ação concluída com sucesso!
                </div>
            )}

            {/* New File Modal */}
            <NewFileModal
                isOpen={showNewFileModal}
                onClose={() => setShowNewFileModal(false)}
                onConfirm={async (fileName) => {
                    try {
                        await createFile(fileName, '# Novo Arquivo\n\nConteúdo inicial...')
                        setShowSuccessMessage(true)
                        setTimeout(() => setShowSuccessMessage(false), 2000)
                    } catch (error) {
                        console.error('Failed to create file:', error)
                        alert('Erro ao criar arquivo. Verifique o console.')
                    }
                }}
            />

            {/* New Folder Modal */}
            <NewFileModal
                isOpen={showNewFolderModal}
                onClose={() => setShowNewFolderModal(false)}
                title="Nova Pasta"
                placeholder="nome-da-pasta"
                onConfirm={async (folderName) => {
                    try {
                        // Criar pasta criando um arquivo .gitkeep dentro dela
                        const gitkeepPath = `${folderName}/.gitkeep`
                        await createFile(gitkeepPath, '')
                        setShowSuccessMessage(true)
                        setTimeout(() => setShowSuccessMessage(false), 2000)
                    } catch (error) {
                        console.error('Failed to create folder:', error)
                        alert('Erro ao criar pasta. Verifique o console.')
                    }
                }}
            />

            {/* Delete Confirm Modal */}
            <DeleteConfirmModal
                isOpen={showDeleteModal}
                fileName={fileToDelete || ''}
                onClose={() => {
                    setShowDeleteModal(false)
                    setFileToDelete(null)
                }}
                onConfirm={confirmDelete}
            />
        </div>
    )
}
